/**
 * Default StepNode Factory
 *
 * Creates default StepNode screens for all 13 V2 StepKinds.
 * Used for:
 * - New theme creation (populating flowDocument with template screens)
 * - Theme preview carousel
 *
 * Each default screen has:
 * - Text content with V2 TypographyStyle appearance paths
 * - Canvas with solid dark backdrop
 * - Header bar (skip + back where applicable)
 * - Action bar (primary button where applicable)
 * - Placeholder translations in 'en-US'
 *
 * @module themes/factories
 */

import type {
  StepNode,
  StepContent,
  FlowDocument,
  LocalizedLabel,
  TypographyStyle,
  TapTarget,
  Backdrop,
  OptionsItem,
  OptionsList,
  LocalizedImage,
} from '@segmently/schema-models/v2';

// ============================================================================
// Shared Helpers
// ============================================================================

const DEFAULT_LANG = 'en-US';

/** Default dark backdrop for all screens */
const defaultBackdrop: Backdrop = {
  kind: 'solid',
  content: { color: '#1a1a1a' },
};

/** Default title typography */
const defaultTitleStyle: TypographyStyle = {
  fontFamily: 'SF Pro',
  fontSize: 21,
  fontWeight: 700,
  color: '#ffffff',
  align: 'center',
};

/** Default subtitle typography */
const defaultSubtitleStyle: TypographyStyle = {
  fontFamily: 'SF Pro',
  fontSize: 17,
  fontWeight: 500,
  color: '#ffffff80',
  align: 'center',
};

/** Default button label typography */
const defaultButtonLabelStyle: TypographyStyle = {
  fontFamily: 'SF Pro',
  fontSize: 17,
  fontWeight: 600,
  color: '#000000',
  align: 'center',
};

/** Default item title typography */
const defaultItemTitleStyle: TypographyStyle = {
  fontFamily: 'SF Pro',
  fontSize: 15,
  fontWeight: 600,
  color: '#ffffff',
  align: 'start',
};

/** Default item subtitle typography */
const defaultItemSubtitleStyle: TypographyStyle = {
  fontFamily: 'SF Pro',
  fontSize: 15,
  fontWeight: 400,
  color: '#ffffff80',
  align: 'start',
};

/** Default skip button label typography */
const defaultSkipLabelStyle: TypographyStyle = {
  fontFamily: 'SF Pro',
  fontSize: 15,
  fontWeight: 500,
  color: '#ffffff',
};

/** Create a localized label with default styling */
function makeLabel(text: string, style: TypographyStyle): LocalizedLabel {
  return {
    translations: { [DEFAULT_LANG]: text },
    kind: 'static',
    appearance: { ...style },
  };
}

/** Create an empty localized image placeholder */
function makeEmptyImage(): LocalizedImage {
  return {
    translations: {},
    appearance: {},
  };
}

/** Create a primary action button (Continue/Next) */
function makePrimaryButton(label = 'Continue'): TapTarget {
  return {
    kind: 'label',
    content: makeLabel(label, defaultButtonLabelStyle),
    appearance: {
      backgroundColor: '#ffc201',
      fullWidth: true,
      borders: { radius: { uniform: 12 } },
    },
    action: { kind: 'advance', edges: [] },
  };
}

/** Create a skip header button */
function makeSkipButton(): TapTarget {
  return {
    kind: 'label',
    content: makeLabel('Skip', defaultSkipLabelStyle),
    appearance: {},
    action: { kind: 'advance', edges: [] },
  };
}

/** Create a back header button */
function makeBackButton(): TapTarget {
  return {
    kind: 'icon',
    content: makeEmptyImage(),
    appearance: {},
    action: { kind: 'retreat', edges: [] },
  };
}

/** Create a default options item */
function makeOptionsItem(title: string, index: number): OptionsItem {
  return {
    title: makeLabel(title, { ...defaultItemTitleStyle }),
    subtitle: makeLabel(`Description ${index + 1}`, { ...defaultItemSubtitleStyle }),
    checkbox: {
      kind: 'circle',
      checked: false,
      appearance: { color: '#555555', isBackgroundFilled: false },
      selectedAppearance: { color: '#ffc201', isBackgroundFilled: true },
    },
    variableValue: `option_${index + 1}`,
  };
}

/** Create default OptionsList */
function makeOptionsList(
  selectionMode: 'browse' | 'one' | 'many',
  columnLayout: 'oneCol' | 'twoCols',
  itemLayout: string,
  itemCount = 3
): OptionsList {
  const items = Array.from({ length: itemCount }, (_, i) =>
    makeOptionsItem(`Option ${i + 1}`, i)
  );
  return {
    selectionMode,
    columnLayout,
    itemLayout: itemLayout as any,
    items,
    appearance: {
      borders: { radius: { uniform: 12 } },
    },
  };
}

/** Common base StepContent fields */
function makeBaseContent(): Pick<StepContent, 'canvas' | 'animated' | 'offlineFirst'> {
  return {
    canvas: {
      background: { ...defaultBackdrop },
    },
    animated: true,
    offlineFirst: false,
  };
}

/** Common header with skip + back */
function makeDefaultHeader() {
  return {
    back: makeBackButton(),
    skip: makeSkipButton(),
  };
}

/** Common action bar with primary button */
function makeDefaultActionBar() {
  return {
    primary: makePrimaryButton(),
  };
}

/** Unique ID counter for default screens */
let idCounter = 0;
function makeScreenId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${idCounter}`;
}

/** Reset ID counter (for deterministic output in tests) */
export function resetIdCounter(): void {
  idCounter = 0;
}

// ============================================================================
// Per-StepKind Factory Functions
// ============================================================================

function createHeroContent(): StepNode {
  const id = makeScreenId('hero_content');
  return {
    id,
    name: 'Hero Content',
    tags: [],
    screenType: 'HeroContent',
    content: {
      ...makeBaseContent(),
      header: makeDefaultHeader(),
      actionBar: makeDefaultActionBar(),
      copy: {
        title: makeLabel('Welcome', defaultTitleStyle),
        subtitle: makeLabel('Get started with your journey', defaultSubtitleStyle),
      } as any,  // subtitle2 not yet in V2 CopyBlock type
      hero: makeEmptyImage(),
    },
    position: { x: 0, y: 0 },
  };
}

function createCarousel(): StepNode {
  const id = makeScreenId('carousel');
  const makeSlide = (title: string, subtitle: string, index: number): any => ({
    progressStart: index,
    progressEnd: index + 1,
    content: {
      image: makeEmptyImage(),
      title: makeLabel(title, defaultTitleStyle),
      subtitle: makeLabel(subtitle, defaultSubtitleStyle),
      detail: makeLabel('', defaultSubtitleStyle),
    },
  });

  return {
    id,
    name: 'Carousel',
    tags: [],
    screenType: 'Carousel',
    content: {
      ...makeBaseContent(),
      header: makeDefaultHeader(),
      // No actionBar — Carousel auto-advances via content.carousel.timer,
      // not a primary button. Seeding an empty actionBar.primary here would
      // create a phantom "CONTINUE" action handle on the canvas that the
      // editor has no section to configure (v2ScreenTypeConfig.Carousel
      // excludes 'actionBar' from its sections array).
      carousel: {
        kind: 'visualCaptionDetail',
        timer: { duration: 5, action: { kind: 'noop', edges: [] } },
        items: [
          makeSlide('Slide 1', 'First slide description', 0),
          makeSlide('Slide 2', 'Second slide description', 1),
          makeSlide('Slide 3', 'Third slide description', 2),
        ],
        appearance: {},
      },
    },
    position: { x: 0, y: 200 },
  };
}

function createListMultiPick(): StepNode {
  const id = makeScreenId('list_multi');
  return {
    id,
    name: 'List Multi Pick',
    tags: [],
    screenType: 'ListMultiPick',
    content: {
      ...makeBaseContent(),
      header: makeDefaultHeader(),
      actionBar: makeDefaultActionBar(),
      copy: {
        title: makeLabel('Select all that apply', defaultTitleStyle),
        subtitle: makeLabel('Choose your preferences', defaultSubtitleStyle),
      },
      options: makeOptionsList('many', 'oneCol', 'TitleSubtitleCheckbox'),
    },
    position: { x: 0, y: 400 },
  };
}

function createListSinglePick(): StepNode {
  const id = makeScreenId('list_single');
  return {
    id,
    name: 'List Single Pick',
    tags: [],
    screenType: 'ListSinglePick',
    content: {
      ...makeBaseContent(),
      header: makeDefaultHeader(),
      actionBar: makeDefaultActionBar(),
      copy: {
        title: makeLabel('Choose one option', defaultTitleStyle),
        subtitle: makeLabel('Select the best fit', defaultSubtitleStyle),
      },
      options: makeOptionsList('one', 'oneCol', 'TitleSubtitleCheckbox'),
    },
    position: { x: 0, y: 600 },
  };
}

function createGridMultiPick(): StepNode {
  const id = makeScreenId('grid_multi');
  return {
    id,
    name: 'Grid Multi Pick',
    tags: [],
    screenType: 'GridMultiPick',
    content: {
      ...makeBaseContent(),
      header: makeDefaultHeader(),
      actionBar: makeDefaultActionBar(),
      copy: {
        title: makeLabel('Select your interests', defaultTitleStyle),
        subtitle: makeLabel('Pick as many as you like', defaultSubtitleStyle),
      },
      options: makeOptionsList('many', 'twoCols', 'MediumImageTitle', 4),
    },
    position: { x: 0, y: 800 },
  };
}

function createGridSinglePick(): StepNode {
  const id = makeScreenId('grid_single');
  return {
    id,
    name: 'Grid Single Pick',
    tags: [],
    screenType: 'GridSinglePick',
    content: {
      ...makeBaseContent(),
      header: makeDefaultHeader(),
      actionBar: makeDefaultActionBar(),
      copy: {
        title: makeLabel('Pick your style', defaultTitleStyle),
        subtitle: makeLabel('Choose one', defaultSubtitleStyle),
      },
      options: makeOptionsList('one', 'twoCols', 'MediumImageTitle', 4),
    },
    position: { x: 0, y: 1000 },
  };
}

function createProgressSteps(): StepNode {
  const id = makeScreenId('progress');
  return {
    id,
    name: 'Progress Steps',
    tags: [],
    screenType: 'ProgressSteps',
    content: {
      ...makeBaseContent(),
      header: makeDefaultHeader(),
      // No actionBar — ProgressSteps auto-advances via content.stepper.timer
      // once the animated progress bar finishes, not via a primary button.
      // Seeding an empty actionBar.primary here would create a phantom
      // "CONTINUE" action handle on the canvas that the editor has no
      // section to configure (v2ScreenTypeConfig.ProgressSteps excludes
      // 'actionBar' from its sections array).
      copy: {
        title: makeLabel('Setting up your profile', defaultTitleStyle),
      },
      stepper: {
        kind: 'orbital',
        timer: { duration: 3, action: { kind: 'advance', edges: [] } },
        items: [
          {
            progressStart: 0,
            progressEnd: 33,
            content: {
              title: makeLabel('Analyzing preferences...', { ...defaultSubtitleStyle, align: 'center' }),
            },
          },
          {
            progressStart: 33,
            progressEnd: 66,
            content: {
              title: makeLabel('Building your plan...', { ...defaultSubtitleStyle, align: 'center' }),
            },
          },
          {
            progressStart: 66,
            progressEnd: 100,
            content: {
              title: makeLabel('Almost ready!', { ...defaultSubtitleStyle, align: 'center' }),
            },
          },
        ],
        label: {
          appearance: { ...defaultSubtitleStyle, fontSize: 14 },
        },
        appearance: {},
      },
    },
    position: { x: 0, y: 1200 },
  };
}

function createTextInput(): StepNode {
  const id = makeScreenId('text_input');
  return {
    id,
    name: 'Text Input',
    tags: [],
    screenType: 'TextInput',
    content: {
      ...makeBaseContent(),
      header: makeDefaultHeader(),
      actionBar: makeDefaultActionBar(),
      copy: {
        title: makeLabel("What's your name?", defaultTitleStyle),
        subtitle: makeLabel('We\'d love to personalize your experience', defaultSubtitleStyle),
      },
      textField: {
        fieldType: 'string',
        placeholder: makeLabel('Enter your name', {
          ...defaultSubtitleStyle,
          fontSize: 16,
          align: 'start',
        }),
        value: '',
        appearance: {
          backgroundColor: '#333333',
        },
      },
    },
    position: { x: 0, y: 1400 },
  };
}

function createWheelPicker(): StepNode {
  const id = makeScreenId('wheel_picker');
  return {
    id,
    name: 'Wheel Picker',
    tags: [],
    screenType: 'WheelPicker',
    content: {
      ...makeBaseContent(),
      header: makeDefaultHeader(),
      actionBar: makeDefaultActionBar(),
      copy: {
        title: makeLabel('Select your preference', defaultTitleStyle),
        subtitle: makeLabel('Scroll to choose', defaultSubtitleStyle),
      },
      roller: {
        dataType: 'string',
        currentValue: '',
        defaultValue: { [DEFAULT_LANG]: 'Option A' },
        separator: ' ',
        wheels: [
          {
            kind: 'list',
            options: {
              localizedOptions: {
                [DEFAULT_LANG]: ['Option A', 'Option B', 'Option C', 'Option D', 'Option E'],
              },
            },
            defaultValue: { [DEFAULT_LANG]: 'Option A' },
          },
        ],
        appearance: {},
        labelAppearance: { ...defaultSubtitleStyle, fontSize: 16 },
      },
    },
    position: { x: 0, y: 1600 },
  };
}

function createHeroList(): StepNode {
  const id = makeScreenId('hero_list');
  return {
    id,
    name: 'Hero List',
    tags: [],
    screenType: 'HeroList',
    content: {
      ...makeBaseContent(),
      header: makeDefaultHeader(),
      actionBar: makeDefaultActionBar(),
      copy: {
        title: makeLabel('Discover Features', defaultTitleStyle),
        subtitle: makeLabel('Here\'s what you get', defaultSubtitleStyle),
      },
      hero: makeEmptyImage(),
      options: makeOptionsList('browse', 'oneCol', 'TitleSubtitle'),
    },
    position: { x: 0, y: 1800 },
  };
}

function createWebEmbed(): StepNode {
  const id = makeScreenId('web_embed');
  return {
    id,
    name: 'Web Embed',
    tags: [],
    screenType: 'WebEmbed',
    content: {
      ...makeBaseContent(),
      embed: {
        callback: {
          action: { kind: 'advance', edges: [] },
        },
      },
    },
    position: { x: 0, y: 2000 },
  };
}

function createPaywall(): StepNode {
  const id = makeScreenId('paywall');
  return {
    id,
    name: 'Paywall',
    tags: [],
    screenType: 'Paywall',
    content: {
      ...makeBaseContent(),
      copy: {
        title: makeLabel('Unlock Premium', defaultTitleStyle),
        subtitle: makeLabel('Get access to all features', defaultSubtitleStyle),
      },
      paywall: {
        products: {
          viewKind: 'Vertical',
          productLayout: 'CheckboxLabels',
          items: [
            {
              productId: 'weekly_subscription',
              topStartLabel: makeLabel('Weekly', { ...defaultItemTitleStyle }),
              bottomStartLabel: makeLabel('$4.99/week', { ...defaultSubtitleStyle, fontSize: 14, align: 'start' }),
              isSelected: false,
              weight: 0,
            },
            {
              productId: 'annual_subscription',
              topStartLabel: makeLabel('Annual', { ...defaultItemTitleStyle }),
              bottomStartLabel: makeLabel('$29.99/year', { ...defaultSubtitleStyle, fontSize: 14, align: 'start' }),
              isSelected: true,
              weight: 1,
            },
          ],
          appearance: {},
        },
        currencyFormat: 'Currency',
        purchaseFlags: [],
        purchaseAction: {
          kind: 'label',
          content: makeLabel('Subscribe', defaultButtonLabelStyle),
          appearance: {
            backgroundColor: '#ffc201',
            fullWidth: true,
            borders: { radius: { uniform: 12 } },
          },
          action: { kind: 'purchase', edges: [] },
        },
        closeButton: {
          kind: 'icon',
          content: makeEmptyImage(),
          appearance: {},
          action: { kind: 'complete', edges: [] },
        },
      },
    },
    position: { x: 0, y: 2200 },
  };
}

function createFlexibleLayout(): StepNode {
  const id = makeScreenId('flexible');
  return {
    id,
    name: 'Flexible Layout',
    tags: [],
    screenType: 'FlexibleLayout',
    content: {
      ...makeBaseContent(),
      // FlexibleLayout starts clean — Header/Footer added as optional sections by user
      flexibleLayout: {
        scrollable: true,
        sections: [],
      },
    },
    position: { x: 0, y: 2400 },
  };
}

// ============================================================================
// Factory Registry
// ============================================================================

/** Map of StepKind to factory function */
const stepKindFactories: Record<string, () => StepNode> = {
  HeroContent: createHeroContent,
  Carousel: createCarousel,
  ListMultiPick: createListMultiPick,
  ListSinglePick: createListSinglePick,
  GridMultiPick: createGridMultiPick,
  GridSinglePick: createGridSinglePick,
  ProgressSteps: createProgressSteps,
  TextInput: createTextInput,
  WheelPicker: createWheelPicker,
  HeroList: createHeroList,
  WebEmbed: createWebEmbed,
  Paywall: createPaywall,
  FlexibleLayout: createFlexibleLayout,
};

/**
 * Create a default StepNode for a given StepKind.
 *
 * @param stepKind - One of the 13 V2 StepKinds
 * @returns Default StepNode with placeholder content
 * @throws Error if stepKind is unknown
 */
export function createDefaultStepNode(stepKind: string): StepNode {
  const factory = stepKindFactories[stepKind];
  if (!factory) {
    throw new Error(`Unknown StepKind: ${stepKind}`);
  }
  return factory();
}

/**
 * All 13 V2 StepKinds in display order.
 */
export const ALL_STEP_KINDS = [
  'HeroContent',
  'Carousel',
  'ListMultiPick',
  'ListSinglePick',
  'GridMultiPick',
  'GridSinglePick',
  'ProgressSteps',
  'TextInput',
  'WheelPicker',
  'HeroList',
  'WebEmbed',
  'Paywall',
  'FlexibleLayout',
] as const;

/**
 * Create a default FlowDocument with all 13 default screens.
 *
 * Used when creating a new V2 theme — populates the theme's flowDocument
 * with one template screen per StepKind.
 *
 * @returns FlowDocument with 13 StepNode screens
 */
export function createDefaultFlowDocument(): FlowDocument {
  resetIdCounter();

  const screens: Record<string, StepNode> = {};
  let firstId = '';

  for (const stepKind of ALL_STEP_KINDS) {
    const node = createDefaultStepNode(stepKind);
    screens[node.id] = node;
    if (!firstId) {
      firstId = node.id;
    }
  }

  return {
    schemaVersion: '2.12.0',
    defaultLanguage: DEFAULT_LANG,
    languages: [DEFAULT_LANG],
    launchScreenId: firstId,
    screens,
    metadata: {},
  };
}
